import { useCallback, useEffect, useMemo, useState } from "react";

import { describeCoreError } from "../../core-api/errors";
import {
  loadEmailIntelligenceDetail,
  loadEmailIntelligenceThreads,
  retryEmailIntelligence,
  researchEmailCustomer,
  reviewEmailIntelligence,
  startEmailIntelligenceCanary,
  startEmailIntelligenceHistoricalBackfill,
  startEmailBusinessBackfill,
  type CustomerProfileCandidate,
  type EmailClassification,
  type EmailIntelligenceAutomation,
  type EmailIntelligenceDetail,
  type EmailIntelligenceState,
  type EmailIntelligenceThread,
  type EmailNature,
} from "../../core-api/email-intelligence";
import "./email-intelligence.css";

const STATE_LABELS: Record<EmailIntelligenceState, string> = {
  PENDING: "待分析",
  RUNNING: "处理中",
  REVIEW_REQUIRED: "待复核",
  ACCEPTED: "已接受",
  CORRECTED: "已修正",
  REJECTED: "已驳回",
  FAILED: "失败",
};

const NATURE_LABELS: Record<EmailNature, string> = {
  NEW_INQUIRY: "新询价",
  CUSTOMER_REPLY: "客户回复",
  STAFF_REPLY: "内部回复",
  AUTOMATED_NOTIFICATION: "自动通知",
  NON_INQUIRY_BUSINESS: "非询价业务",
  MARKETING: "营销邮件",
  SPAM_OR_PHISHING: "垃圾或钓鱼",
  UNKNOWN: "待判断",
};

const PROFILE_LABELS: Record<string, string> = {
  BUSINESS: "企业",
  UNIVERSITY: "大学",
  RESEARCH_INSTITUTION: "研究机构",
  GOVERNMENT: "政府机构",
  NONPROFIT: "非营利机构",
  DISTRIBUTOR: "经销商",
  PURCHASING_AGENT: "采购代理",
  INDIVIDUAL: "个人",
  UNKNOWN: "未知",
  PROSPECT: "潜在客户",
  ACTIVE_INQUIRY: "活跃询价",
  NEGOTIATION: "商务洽谈",
  EXISTING_CUSTOMER: "现有客户",
  DORMANT: "沉默客户",
  NONE: "无",
  LOW: "低",
  MEDIUM: "中",
  HIGH: "高",
  STRATEGIC: "战略级",
  NEGATIVE: "负向",
  NEUTRAL: "中性",
  POSITIVE: "正向",
};

type Filter = "ALL" | EmailIntelligenceState;

export function EmailIntelligenceReview({
  canReview,
  canRun,
}: {
  canReview: boolean;
  canRun: boolean;
}) {
  const [threads, setThreads] = useState<EmailIntelligenceThread[]>([]);
  const [automation, setAutomation] = useState<EmailIntelligenceAutomation>();
  const [selectedId, setSelectedId] = useState("");
  const [detail, setDetail] = useState<EmailIntelligenceDetail>();
  const [filter, setFilter] = useState<Filter>("ALL");
  const [loading, setLoading] = useState(true);
  const [detailLoading, setDetailLoading] = useState(false);
  const [busy, setBusy] = useState(false);
  const [failure, setFailure] = useState("");
  const [notice, setNotice] = useState("");
  const [reason, setReason] = useState("");
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState<CustomerProfileCandidate>();

  const refresh = useCallback(async (signal?: AbortSignal) => {
    setLoading(true);
    setFailure("");
    try {
      const result = await loadEmailIntelligenceThreads(undefined, signal);
      setThreads(result.items);
      setAutomation(result.automation);
      setSelectedId((current) =>
        result.items.some(({ conversationId }) => conversationId === current)
          ? current
          : (result.items[0]?.conversationId ?? ""),
      );
    } catch (error) {
      if (!signal?.aborted) setFailure(describeCoreError(error));
    } finally {
      if (!signal?.aborted) setLoading(false);
    }
  }, []);

  useEffect(() => {
    const controller = new AbortController();
    void refresh(controller.signal);
    return () => controller.abort();
  }, [refresh]);

  useEffect(() => {
    if (!selectedId) {
      setDetail(undefined);
      return;
    }
    const controller = new AbortController();
    setDetailLoading(true);
    setFailure("");
    loadEmailIntelligenceDetail(selectedId, controller.signal)
      .then((next) => {
        setDetail(next);
        setDraft(next.result?.customerProfileCandidate);
        setReason("");
        setEditing(false);
      })
      .catch((error) => {
        if (!controller.signal.aborted) setFailure(describeCoreError(error));
      })
      .finally(() => {
        if (!controller.signal.aborted) setDetailLoading(false);
      });
    return () => controller.abort();
  }, [selectedId]);

  const filtered = useMemo(
    () =>
      threads.filter((thread) => filter === "ALL" || thread.state === filter),
    [filter, threads],
  );

  const submitReview = async (
    decision: "ACCEPT" | "ACCEPT_WITH_CHANGES" | "REJECT",
  ) => {
    if (!detail?.invocationId || !reason.trim()) {
      setFailure("请先填写本次人工复核依据。此说明会进入审计记录。");
      return;
    }
    setBusy(true);
    setFailure("");
    setNotice("");
    try {
      await reviewEmailIntelligence(detail.conversationId, {
        expectedInvocationId: detail.invocationId,
        decision,
        changes:
          decision === "ACCEPT_WITH_CHANGES" && draft
            ? { customerProfileCandidate: draft }
            : {},
        reason: reason.trim(),
      });
      setNotice(
        decision === "ACCEPT"
          ? "建议已接受，仅保存复核记录。"
          : decision === "REJECT"
            ? "建议已驳回，不会创建任何业务对象。"
            : "修正后的建议已保存，仅作为人工复核证据。",
      );
      await refresh();
      setDetail(await loadEmailIntelligenceDetail(detail.conversationId));
      setEditing(false);
    } catch (error) {
      setFailure(describeCoreError(error));
    } finally {
      setBusy(false);
    }
  };

  const retry = async () => {
    if (!detail) return;
    setBusy(true);
    setFailure("");
    try {
      await retryEmailIntelligence(detail.conversationId);
      setNotice("已进入单线程重新分析队列。旧结果和旧复核记录均保留。");
      await refresh();
      setDetail(await loadEmailIntelligenceDetail(detail.conversationId));
    } catch (error) {
      setFailure(describeCoreError(error));
    } finally {
      setBusy(false);
    }
  };

  const researchCustomer = async () => {
    if (!detail) return;
    setBusy(true);
    setFailure("");
    setNotice("");
    try {
      await researchEmailCustomer(detail.conversationId);
      setNotice("已启动客户公开信息调查，正在等待 AI 形成带来源的判断…");
      window.setTimeout(() => {
        void loadEmailIntelligenceDetail(detail.conversationId)
          .then(setDetail)
          .catch(() => undefined);
      }, 2_500);
    } catch (error) {
      setFailure(describeCoreError(error));
    } finally {
      setBusy(false);
    }
  };

  const startCanary = async () => {
    setBusy(true);
    setFailure("");
    try {
      await startEmailIntelligenceCanary();
      setNotice("20 封灰度已进入 SPAM 只读回填与邮件智能队列。");
    } catch (error) {
      setFailure(describeCoreError(error));
    } finally {
      setBusy(false);
    }
  };

  const startFull = async () => {
    setBusy(true);
    setFailure("");
    try {
      const business = (await startEmailBusinessBackfill()) as {
        queued?: number;
      };
      const historical = (await startEmailIntelligenceHistoricalBackfill(
        20,
      )) as {
        selectedMessages?: number;
      };
      setNotice(
        `已重放 ${business.queued ?? 0} 个既有有效结果，并选择 ${historical.selectedMessages ?? 0} 封新邮件继续分析。`,
      );
      await refresh();
    } catch (error) {
      setFailure(describeCoreError(error));
    } finally {
      setBusy(false);
    }
  };

  return (
    <main className="email-intelligence" aria-label="邮件智能复核">
      <header className="email-intelligence__header">
        <div>
          <p className="email-intelligence__eyebrow">
            只读邮件智能
          </p>
          <h1>邮件智能复核</h1>
          <p>
            将 INBOX 与最近 30 天 SPAM 汇成线程证据。AI
            提供分类和客户画像建议；身份匹配、去重和内部 CRM
            动作由确定性规则执行，客户与供应商外发保持关闭。
          </p>
        </div>
        <div className="email-intelligence__guardrail" aria-label="安全边界">
          <span>Gmail 只读</span>
          {automation ? (
            <>
              <span
                className={automation.autoApplyKillSwitch ? "" : "is-warning"}
              >
                {automation.autoApplyKillSwitch
                  ? "自动应用关闭"
                  : "自动应用未锁定"}
              </span>
              <span
                className={
                  automation.externalDraftKillSwitch ? "" : "is-warning"
                }
              >
                {automation.externalDraftKillSwitch
                  ? "外部草稿关闭"
                  : "外部草稿未锁定"}
              </span>
            </>
          ) : (
            <span>正在确认安全边界</span>
          )}
          {automation ? (
            <strong className={automation.enabled ? "" : "is-warning"}>
              {automation.enabled
                ? automation.phase === "CANARY"
                  ? "20 封灰度"
                  : "历史回填"
                : "自动化已停止"}
            </strong>
          ) : null}
          {automation?.businessAutomationEnabled ? (
            <strong>内部业务自动化已启用</strong>
          ) : null}
        </div>
      </header>

      <section className="email-intelligence__toolbar" aria-label="队列筛选">
        <div className="email-intelligence__filters">
          {(
            [
              "ALL",
              "PENDING",
              "RUNNING",
              "REVIEW_REQUIRED",
              "FAILED",
              "ACCEPTED",
            ] as Filter[]
          ).map((value) => (
            <button
              key={value}
              type="button"
              className={filter === value ? "is-active" : ""}
              onClick={() => setFilter(value)}
            >
              {value === "ALL" ? "全部" : STATE_LABELS[value]}
              <span>
                {value === "ALL"
                  ? threads.length
                  : threads.filter(({ state }) => state === value).length}
              </span>
            </button>
          ))}
        </div>
        {canRun && automation?.phase === "CANARY" ? (
          <button
            className="email-intelligence__canary"
            type="button"
            disabled={busy || !automation.enabled}
            onClick={() => void startCanary()}
          >
            启动 20 封灰度
          </button>
        ) : null}
        {canRun && automation?.phase === "FULL" ? (
          <button
            className="email-intelligence__canary"
            type="button"
            disabled={busy || !automation.enabled}
            onClick={() => void startFull()}
          >
            继续 FULL 处理
          </button>
        ) : null}
      </section>

      {failure ? (
        <div className="email-intelligence__message is-error">{failure}</div>
      ) : null}
      {notice ? (
        <div className="email-intelligence__message is-success">{notice}</div>
      ) : null}

      <div className="email-intelligence__layout">
        <aside className="email-thread-list" aria-label="邮件线程队列">
          {loading ? (
            <p className="email-intelligence__empty">正在加载邮件线程…</p>
          ) : null}
          {!loading && filtered.length === 0 ? (
            <div className="email-intelligence__empty">
              <strong>当前筛选没有线程</strong>
              <span>新邮件进入后会自动出现在这里。</span>
            </div>
          ) : null}
          {filtered.map((thread) => (
            <button
              key={thread.conversationId}
              type="button"
              className={
                selectedId === thread.conversationId
                  ? "email-thread-card is-selected"
                  : "email-thread-card"
              }
              onClick={() => setSelectedId(thread.conversationId)}
            >
              <span
                className={`email-state email-state--${thread.state.toLowerCase()}`}
              >
                {stateLabel(thread.state)}
              </span>
              <strong>{thread.subject || "（无主题）"}</strong>
              <span className="email-thread-card__meta">
                {thread.messageCount} 封 ·{" "}
                {thread.latestDirection === "OUTBOUND"
                  ? "我方回复"
                  : "对方来信"}
              </span>
              <span className="email-thread-card__signal">
                {thread.primaryNature
                  ? natureLabel(thread.primaryNature)
                  : "尚未分类"}
                {thread.riskLevel
                  ? ` · 风险${profileLabel(thread.riskLevel)}`
                  : ""}
              </span>
              <small>{formatDate(thread.latestAt)}</small>
            </button>
          ))}
        </aside>

        <section className="email-evidence-panel" aria-label="线程证据">
          {detailLoading ? (
            <p className="email-intelligence__empty">正在加载脱敏证据…</p>
          ) : null}
          {!detailLoading && !detail ? (
            <div className="email-intelligence__empty">
              <strong>选择一个线程开始复核</strong>
            </div>
          ) : null}
          {detail ? <ThreadEvidence detail={detail} /> : null}
        </section>

        <aside className="customer-signal-panel" aria-label="客户画像建议">
          {detail ? (
            <ProfileReview
              detail={detail}
              draft={draft}
              setDraft={setDraft}
              editing={editing}
              setEditing={setEditing}
              reason={reason}
              setReason={setReason}
              busy={busy}
              canReview={canReview}
              onReview={submitReview}
              onRetry={retry}
              onResearchCustomer={researchCustomer}
            />
          ) : (
            <div className="email-intelligence__empty">
              画像建议将在这里显示。
            </div>
          )}
        </aside>
      </div>
    </main>
  );
}

function ThreadEvidence({ detail }: { detail: EmailIntelligenceDetail }) {
  const classifications = new Map(
    (detail.result?.messageClassifications ?? []).map((item) => [
      item.messageRef,
      item,
    ]),
  );
  return (
    <>
      <header className="email-evidence-panel__header">
        <span
          className={`email-state email-state--${detail.state.toLowerCase()}`}
        >
          {stateLabel(detail.state)}
        </span>
        <h2>{detail.subject || "（无主题）"}</h2>
        <div className="email-evidence-panel__facts">
          <span>
            <strong>客户邮箱</strong>
            {detail.customerEmail || "未识别"}
          </span>
          <span>{detail.messages.length} 封已进入当前分析窗口</span>
        </div>
      </header>
      {detail.originalInquiry ? (
        <OriginalInquiry evidence={detail.originalInquiry} />
      ) : null}
      <ol className="email-evidence-rail">
        {detail.messages.map((message) => (
          <li key={message.messageRef}>
            <span
              className={`email-evidence-rail__dot is-${message.direction.toLowerCase()}`}
            />
            <article>
              <div className="email-evidence-rail__meta">
                <strong>
                  {message.direction === "OUTBOUND" ? "我方回复" : "对方来信"}
                </strong>
                <span>
                  {message.mailboxCategory === "SPAM" ? "SPAM" : "INBOX"}
                </span>
                <time>{formatDate(message.observedAt)}</time>
              </div>
              <div className="email-evidence-rail__addresses">
                <span>
                  <strong>发件人</strong> {message.senderEmail || "未识别"}
                </span>
                <span>
                  <strong>收件人</strong>{" "}
                  {message.recipientEmails?.join(", ") || "未识别"}
                </span>
              </div>
              <h3>{message.subject || "（无主题）"}</h3>
              <EmailBody message={message} />
              <Classification
                classification={classifications.get(message.messageRef)}
              />
              {message.attachmentSummary.count > 0 ? (
                <small>
                  附件元数据：{message.attachmentSummary.count} 个 ·{" "}
                  {message.attachmentSummary.contentTypes.join("、") ||
                    "类型未知"}
                </small>
              ) : null}
            </article>
          </li>
        ))}
      </ol>
    </>
  );
}

function EmailBody({
  message,
}: {
  message: EmailIntelligenceDetail["messages"][number];
}) {
  const current =
    message.bodyBlocks?.filter(({ kind }) => kind === "CURRENT_CONTENT") ?? [];
  const supporting =
    message.bodyBlocks?.filter(({ kind }) => kind !== "CURRENT_CONTENT") ?? [];
  const body =
    message.bodyText ||
    current
      .map(({ text }) => text)
      .filter(Boolean)
      .join("\n\n") ||
    message.excerpt;
  return (
    <div className="email-message-body">
      {body ? (
        <p className="email-evidence-rail__excerpt">{body}</p>
      ) : (
        <p className="email-evidence-rail__excerpt is-missing">
          没有可显示的邮件正文，当前只能依据主题、发件人和附件元数据复核。
        </p>
      )}
      {supporting.length > 0 ? (
        <details className="email-message-body__details">
          <summary>查看引用内容、转发内容和签名</summary>
          {supporting.map((block) => (
            <section key={block.blockRef}>
              <small>
                {bodyBlockLabel(block.kind)}
                {block.truncated ? " · 内容已截断" : ""}
              </small>
              <p>{block.text || "（空内容）"}</p>
            </section>
          ))}
        </details>
      ) : null}
      {message.bodySource === "HTML_TO_SAFE_TEXT" ? (
        <small className="email-message-body__source">
          HTML 邮件已转换为安全纯文本显示
        </small>
      ) : null}
    </div>
  );
}

function OriginalInquiry({
  evidence,
}: {
  evidence: NonNullable<EmailIntelligenceDetail["originalInquiry"]>;
}) {
  return (
    <section className="original-inquiry" aria-label="原始 Web Inquiry">
      <div className="original-inquiry__header">
        <div>
          <strong>原始 Web Inquiry</strong>
          <small>
            {formatDate(evidence.submittedAt)} · {evidence.sourceInquiryId}
          </small>
        </div>
        <span>来源记录</span>
      </div>
      <dl className="original-inquiry__facts">
        <InquiryFact label="客户邮箱" value={evidence.email} />
        <InquiryFact label="联系人" value={evidence.contactName} />
        <InquiryFact label="公司" value={evidence.companyName} />
        <InquiryFact label="产品" value={evidence.productDisplayName} />
        <InquiryFact label="CAS" value={evidence.cas} />
        <InquiryFact label="数量" value={evidence.quantity} />
      </dl>
      <div className="original-inquiry__comment">
        <strong>用户填写的 Comment</strong>
        <p>{evidence.comment || "未提供 comment"}</p>
      </div>
      {evidence.researchUse ? (
        <div className="original-inquiry__secondary">
          <strong>用途</strong>
          <p>{evidence.researchUse}</p>
        </div>
      ) : null}
    </section>
  );
}

function InquiryFact({
  label,
  value,
}: {
  label: string;
  value: string | null;
}) {
  return (
    <div>
      <dt>{label}</dt>
      <dd>{value || "未提供"}</dd>
    </div>
  );
}

function bodyBlockLabel(
  kind: NonNullable<
    EmailIntelligenceDetail["messages"][number]["bodyBlocks"]
  >[number]["kind"],
) {
  return {
    QUOTED_REPLY: "引用回复",
    FORWARDED_MESSAGE: "转发内容",
    SIGNATURE: "签名",
    CURRENT_CONTENT: "当前正文",
  }[kind];
}

function Classification({
  classification,
}: {
  classification?: EmailClassification;
}) {
  if (!classification)
    return <div className="email-classification is-pending">等待 AI 分类</div>;
  return (
    <div
      className={`email-classification nature-${classification.nature.toLowerCase()}`}
    >
      <strong>{natureLabel(classification.nature)}</strong>
      <span>{percent(classification.confidence)}</span>
      <p>{classification.evidenceSummary}</p>
    </div>
  );
}

function ProfileReview({
  detail,
  draft,
  setDraft,
  editing,
  setEditing,
  reason,
  setReason,
  busy,
  canReview,
  onReview,
  onRetry,
  onResearchCustomer,
}: {
  detail: EmailIntelligenceDetail;
  draft?: CustomerProfileCandidate;
  setDraft: (value: CustomerProfileCandidate) => void;
  editing: boolean;
  setEditing: (value: boolean) => void;
  reason: string;
  setReason: (value: string) => void;
  busy: boolean;
  canReview: boolean;
  onReview: (
    decision: "ACCEPT" | "ACCEPT_WITH_CHANGES" | "REJECT",
  ) => Promise<void>;
  onRetry: () => Promise<void>;
  onResearchCustomer: () => Promise<void>;
}) {
  const profile = draft;
  if (!profile) {
    return (
      <div className="email-intelligence__empty">
        <strong>
          {detail.state === "FAILED" ? "本次分析失败" : "画像建议尚未生成"}
        </strong>
        {canReview ? (
          <button type="button" disabled={busy} onClick={() => void onRetry()}>
            要求重新分析
          </button>
        ) : null}
      </div>
    );
  }
  const update = (key: keyof CustomerProfileCandidate, value: string) =>
    setDraft({ ...profile, [key]: value });
  const updateList = (
    key: "riskSignals" | "missingInformation",
    value: string,
  ) =>
    setDraft({
      ...profile,
      [key]: value
        .split("\n")
        .map((item) => item.trim())
        .filter(Boolean),
    });
  return (
    <>
      <header className="customer-signal-panel__header">
        <span>客户画像建议</span>
        <strong>{percent(profile.confidence)}</strong>
      </header>
      {detail.customerResearch ? (
        <CustomerResearchSummary research={detail.customerResearch} />
      ) : null}
      <h2>{profile.organizationName || "机构名称待确认"}</h2>
      <p className="customer-signal-panel__summary">{profile.summary}</p>
      {canReview ? (
        <button
          type="button"
          className="customer-research-button"
          disabled={busy}
          onClick={() => void onResearchCustomer()}
        >
          检索客户公开信息
        </button>
      ) : null}
      {editing ? (
        <div className="customer-signal-form">
          <label>
            机构名称
            <input
              value={profile.organizationName ?? ""}
              onChange={(event) =>
                update("organizationName", event.target.value)
              }
            />
          </label>
          <label>
            可能国家
            <input
              maxLength={2}
              value={profile.probableCountryCode ?? ""}
              onChange={(event) =>
                update("probableCountryCode", event.target.value.toUpperCase())
              }
            />
          </label>
          <label className="customer-signal-form__wide">
            画像摘要
            <textarea
              value={profile.summary}
              maxLength={800}
              onChange={(event) => update("summary", event.target.value)}
            />
          </label>
          <SelectField
            label="机构类型"
            value={profile.organizationType}
            options={[
              "BUSINESS",
              "UNIVERSITY",
              "RESEARCH_INSTITUTION",
              "GOVERNMENT",
              "NONPROFIT",
              "DISTRIBUTOR",
              "PURCHASING_AGENT",
              "INDIVIDUAL",
              "UNKNOWN",
            ]}
            onChange={(value) => update("organizationType", value)}
          />
          <SelectField
            label="关系阶段"
            value={profile.relationshipStage}
            options={[
              "UNKNOWN",
              "PROSPECT",
              "ACTIVE_INQUIRY",
              "NEGOTIATION",
              "EXISTING_CUSTOMER",
              "DORMANT",
            ]}
            onChange={(value) => update("relationshipStage", value)}
          />
          <SelectField
            label="采购意向"
            value={profile.purchaseIntent}
            options={["NONE", "LOW", "MEDIUM", "HIGH", "UNKNOWN"]}
            onChange={(value) => update("purchaseIntent", value)}
          />
          <SelectField
            label="商业潜力"
            value={profile.commercialPotential}
            options={["LOW", "MEDIUM", "HIGH", "STRATEGIC", "UNKNOWN"]}
            onChange={(value) => update("commercialPotential", value)}
          />
          <SelectField
            label="互动质量"
            value={profile.engagementQuality}
            options={["NEGATIVE", "NEUTRAL", "POSITIVE", "UNKNOWN"]}
            onChange={(value) => update("engagementQuality", value)}
          />
          <SelectField
            label="风险等级"
            value={profile.riskLevel}
            options={["LOW", "MEDIUM", "HIGH", "UNKNOWN"]}
            onChange={(value) => update("riskLevel", value)}
          />
          <label className="customer-signal-form__wide">
            风险信号（每行一项）
            <textarea
              value={profile.riskSignals.join("\n")}
              onChange={(event) =>
                updateList("riskSignals", event.target.value)
              }
            />
          </label>
          <label className="customer-signal-form__wide">
            待补信息（每行一项）
            <textarea
              value={profile.missingInformation.join("\n")}
              onChange={(event) =>
                updateList("missingInformation", event.target.value)
              }
            />
          </label>
        </div>
      ) : (
        <dl className="customer-signal-grid">
          <ProfileFact
            label="机构类型"
            value={profileLabel(profile.organizationType)}
          />
          <ProfileFact
            label="可能国家"
            value={profile.probableCountryCode || "待确认"}
          />
          <ProfileFact
            label="关系阶段"
            value={profileLabel(profile.relationshipStage)}
          />
          <ProfileFact
            label="采购意向"
            value={profileLabel(profile.purchaseIntent)}
          />
          <ProfileFact
            label="商业潜力"
            value={profileLabel(profile.commercialPotential)}
          />
          <ProfileFact
            label="互动质量"
            value={profileLabel(profile.engagementQuality)}
          />
        </dl>
      )}
      <SignalList
        title="风险信号"
        items={profile.riskSignals}
        empty="暂未发现明确风险"
      />
      <SignalList
        title="待补信息"
        items={profile.missingInformation}
        empty="没有额外缺失信息"
      />
      {canReview ? (
        <div className="customer-review-actions">
          <label>
            人工复核依据
            <textarea
              value={reason}
              maxLength={1000}
              onChange={(event) => setReason(event.target.value)}
              placeholder="说明接受、修正或驳回的业务依据"
            />
          </label>
          <div>
            <button
              type="button"
              disabled={busy}
              onClick={() => void onReview("ACCEPT")}
            >
              接受建议
            </button>
            <button
              type="button"
              disabled={busy}
              onClick={() =>
                editing
                  ? void onReview("ACCEPT_WITH_CHANGES")
                  : setEditing(true)
              }
            >
              {editing ? "保存修正" : "修正画像"}
            </button>
            <button
              type="button"
              disabled={busy}
              className="is-danger"
              onClick={() => void onReview("REJECT")}
            >
              驳回建议
            </button>
            <button
              type="button"
              disabled={busy}
              onClick={() => void onRetry()}
            >
              要求重新分析
            </button>
          </div>
          <small>
            复核只更新 AI
            Review；正式业务对象由独立的确定性规则处理，复核按钮本身不会重复创建客户、询单、案件或分配。
          </small>
        </div>
      ) : (
        <p className="customer-review-readonly">当前账号为只读权限。</p>
      )}
    </>
  );
}

function CustomerResearchSummary({
  research,
}: {
  research: NonNullable<EmailIntelligenceDetail["customerResearch"]>;
}) {
  const result = research.result ?? {};
  const assessment = (result.organizationAssessment ?? {}) as Record<
    string,
    unknown
  >;
  const country = (assessment.probableCountry ?? {}) as Record<string, unknown>;
  const quality = (result.informationQuality ?? {}) as Record<string, unknown>;
  const sources = Array.isArray(result.sources) ? result.sources : [];
  return (
    <section
      className="customer-research-summary"
      aria-label="客户公开信息调查结果"
    >
      <strong>
        公开信息调查 {research.status === "COMPLETED" ? "已完成" : "处理中"}
      </strong>
      {research.result ? (
        <>
          <div>
            身份可信度：{percent(Number(assessment.identityConfidence ?? 0))}
          </div>
          <div>
            信息质量：{percent(Number(quality.legitimacyConfidence ?? 0))}
          </div>
          <div>可能国家：{String(country.countryCode || "待确认")}</div>
          <div>公开证据：{sources.length} 条</div>
          <small>调查结果仅供人工确认，不会自动创建客户或联系人。</small>
        </>
      ) : (
        <small>AI 正在根据邮箱域名和公开来源生成判断。</small>
      )}
    </section>
  );
}

function SelectField({
  label,
  value,
  options,
  onChange,
}: {
  label: string;
  value: string;
  options: string[];
  onChange: (value: string) => void;
}) {
  return (
    <label>
      {label}
      <select value={value} onChange={(event) => onChange(event.target.value)}>
        {options.map((option) => (
          <option key={option} value={option}>
            {profileLabel(option)}
          </option>
        ))}
      </select>
    </label>
  );
}

function ProfileFact({ label, value }: { label: string; value: string }) {
  return (
    <div>
      <dt>{label}</dt>
      <dd>{value}</dd>
    </div>
  );
}

function SignalList({
  title,
  items,
  empty,
}: {
  title: string;
  items: string[];
  empty: string;
}) {
  return (
    <section className="customer-signal-list">
      <h3>{title}</h3>
      {items.length ? (
        <ul>
          {items.map((item) => (
            <li key={item}>{item}</li>
          ))}
        </ul>
      ) : (
        <p>{empty}</p>
      )}
    </section>
  );
}

function profileLabel(value: string) {
  return PROFILE_LABELS[value] ?? "未识别状态";
}

function stateLabel(value: string) {
  return STATE_LABELS[value as EmailIntelligenceState] ?? "未识别状态";
}

function natureLabel(value: string) {
  return NATURE_LABELS[value as EmailNature] ?? "未识别状态";
}

function percent(value: number) {
  return `${Math.round(value * 100)}%`;
}

function formatDate(value: string) {
  return new Intl.DateTimeFormat("zh-CN", {
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
  }).format(new Date(value));
}
